import React, { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, orderBy, query, Timestamp, where } from "firebase/firestore";
import { ChevronLeft, ChevronRight, Clock, UserRoundCheck } from "lucide-react";
import { db } from "../../Lib/Firebase";
import { chipClass, labelForPackage } from "../components/Primitives";
import { type SlotDoc } from "../types";
import { endOfWeek, fmtDateDisplay, fmtRange, fmtTime, getWeekDays, isSameDay, shiftWeek } from "../utils/date";

type Props = { weekStart: Date; setWeekStart: (date: Date) => void };

export default function SlotsTab({ weekStart, setWeekStart }: Props) {
    const weekEnd = useMemo(() => endOfWeek(weekStart), [weekStart]);
    const days = useMemo(() => getWeekDays(weekStart), [weekStart]);
    const [slots, setSlots] = useState<Array<SlotDoc & { id: string }>>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        const slotsQuery = query(
            collection(db, "slots"),
            where("start", ">=", Timestamp.fromDate(weekStart)),
            where("start", "<", Timestamp.fromDate(weekEnd)),
            orderBy("start", "asc"),
        );
        return onSnapshot(slotsQuery, (snapshot) => {
            setSlots(snapshot.docs.map((item) => ({ id: item.id, ...(item.data() as SlotDoc) })));
            setLoading(false);
        }, () => setLoading(false));
    }, [weekEnd, weekStart]);

    return <div className="space-y-4">
        <section className="rounded-2xl border border-cyan-300/20 bg-cyan-300/[0.06] p-4 text-sm text-cyan-50">
            <div className="flex items-center gap-2 font-bold"><UserRoundCheck size={18} />Partner-owned availability</div>
            <p className="mt-1 text-cyan-100/80">Interviewers publish and withdraw their own slots from the Partner Dashboard. Admins can monitor availability here and allocate matching slots from Interview Requests.</p>
        </section>
        <div className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3">
            <button className="rounded-xl border border-white/20 p-1" onClick={() => setWeekStart(shiftWeek(weekStart, -1))}><ChevronLeft className="h-4 w-4" /></button>
            <button className="rounded-xl border border-white/20 p-1" onClick={() => setWeekStart(shiftWeek(weekStart, 1))}><ChevronRight className="h-4 w-4" /></button>
            <span className="ml-2 text-sm font-semibold">{fmtRange(weekStart, weekEnd)}</span>
        </div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-7">
            {days.map((day) => {
                const daySlots = slots.filter((slot) => isSameDay(slot.start.toDate(), day)).sort((a, b) => a.start.toMillis() - b.start.toMillis());
                return <div key={day.toISOString()} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3">
                    <div className="mb-2 flex items-center justify-between"><span className="text-sm font-semibold">{day.toLocaleDateString(undefined, { weekday: "short" })}</span><span className="text-xs text-white/70">{fmtDateDisplay(day)}</span></div>
                    {loading ? <p className="text-sm text-white/70">Loading…</p> : !daySlots.length ? <p className="rounded-xl border border-dashed border-white/15 py-8 text-center text-xs text-white/50">No partner slots</p> : <ul className="space-y-2">{daySlots.map((slot) => {
                        const packages = slot.packageTypes?.length ? slot.packageTypes : slot.tier ? [slot.tier.toLowerCase() as "service" | "product" | "maang"] : [];
                        return <li key={slot.id} className="rounded-xl border border-white/10 bg-white/[0.06] p-3">
                            <p className="text-sm font-semibold">{slot.interviewerName || "Partner interviewer"}</p>
                            <p className="mt-1 inline-flex items-center gap-1 text-xs text-white/80"><Clock className="h-3 w-3" />{fmtTime(slot.start.toDate())} – {fmtTime(slot.end.toDate())}</p>
                            {slot.details && <p className="mt-1 text-xs text-white/70">{slot.details}</p>}
                            <div className="mt-2 flex flex-wrap gap-1">{packages.map((item) => <span key={item} className={chipClass(item)}>{labelForPackage(item)}</span>)}<span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] ${slot.status === "published" ? "bg-emerald-500/15 text-emerald-300" : "bg-yellow-500/15 text-yellow-300"}`}>{slot.status}</span></div>
                        </li>;
                    })}</ul>}
                </div>;
            })}
        </div>
    </div>;
}
